import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Wine, Loader2, Minus, Plus, Coins } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { useAutoTranslate } from "@/lib/auto-translate";

export const Route = createFileRoute("/events/$eventId/drinks")({
  head: () => ({ meta: [{ title: "İçecek Satın Al — JUËN" }] }),
  component: DrinksCheckoutPage,
});

interface EventRow {
  id: string;
  name: string;
  starts_at: string;
  venue: string | null;
}

interface DrinkRow {
  id: string;
  name: string;
  description: string | null;
  price_kurus: number;
}

function DrinksCheckoutPage() {
  const { eventId } = Route.useParams();
  const { user, profile, loading, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const [event, setEvent] = useState<EventRow | null>(null);
  const [drinks, setDrinks] = useState<DrinkRow[]>([]);
  const [drinkQty, setDrinkQty] = useState<Record<string, number>>({});
  const [pageLoading, setPageLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const tc = useAutoTranslate([event?.name, ...drinks.flatMap((d) => [d.name, d.description])]);

  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/login" });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    void (async () => {
      const [{ data: ev }, { data: dr }] = await Promise.all([
        supabase.from("events").select("id,name,starts_at,venue").eq("id", eventId).maybeSingle(),
        supabase.from("drinks").select("*").or(`event_id.eq.${eventId},event_id.is.null`).eq("is_active", true).order("sort_order"),
      ]);
      setEvent(ev as EventRow | null);
      setDrinks((dr as DrinkRow[]) ?? []);
      setPageLoading(false);
    })();
  }, [eventId, user]);

  const totalKurus = useMemo(
    () => drinks.reduce((sum, d) => sum + (drinkQty[d.id] ?? 0) * d.price_kurus, 0),
    [drinks, drinkQty],
  );

  const totalItems = useMemo(
    () => Object.values(drinkQty).reduce((s, n) => s + n, 0),
    [drinkQty],
  );

  if (loading || !user || pageLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <h1 className="text-2xl font-bold">{t("events.notFoundTitle")}</h1>
          <Button asChild className="mt-4">
            <Link to="/events">{t("events.backToEventsBtn")}</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (profile?.membership_status !== "approved") {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <h1 className="text-2xl font-bold">{t("events.approvedMembershipRequired")}</h1>
          <p className="mt-2 text-muted-foreground">{t("events.approvedMembershipForDrinks")}</p>
        </div>
      </div>
    );
  }

  const setQty = (drinkId: string, delta: number) => {
    setDrinkQty((prev) => {
      const next = Math.max(0, (prev[drinkId] ?? 0) + delta);
      return { ...prev, [drinkId]: next };
    });
  };

  const pointsNeeded = Math.ceil(totalKurus / 100);
  const balance = profile?.points_balance ?? 0;

  // Points payment only on Friday (5) and Saturday (6) — Europe/Istanbul
  const istanbulDow = (() => {
    const fmt = new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone: "Europe/Istanbul" });
    const map: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    return map[fmt.format(new Date())] ?? new Date().getDay();
  })();
  const isPointsDay = istanbulDow === 5 || istanbulDow === 6;

  const handleCheckout = async () => {
    if (totalItems === 0) {
      toast.error(t("events.selectAtLeastOneDrink"));
      return;
    }
    if (balance < pointsNeeded) {
      toast.error(t("events.insufficientJ", { needed: pointsNeeded, balance }));
      return;
    }
    setSubmitting(true);
    try {
      const drinkPayload = drinks
        .filter((d) => (drinkQty[d.id] ?? 0) > 0)
        .map((d) => ({ drink_id: d.id, quantity: drinkQty[d.id] }));

      const { data: createRes, error: createErr } = await supabase.rpc(
        "create_drinks_order",
        { _event_id: eventId, _drinks: drinkPayload },
      );
      if (createErr) throw createErr;
      const orderId = (createRes as { order_id?: string } | null)?.order_id;
      if (!orderId) throw new Error(t("events.drinksOrderFailed"));

      const { error: payErr } = await supabase.rpc(
        "pay_order_with_points_drinks",
        { _order_id: orderId },
      );
      if (payErr) throw payErr;

      await refreshProfile();
      toast.success(t("events.drinksReady"));
      void navigate({ to: "/vouchers" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("common.error");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-4 py-10">
        <Link
          to="/events/$eventId"
          params={{ eventId }}
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> {t("events.backToEvent")}
        </Link>

        <h1 className="text-3xl font-bold tracking-tight">{t("events.buyDrinksTitle")}</h1>
        <p className="mt-2 text-muted-foreground">{tc(event.name)}</p>

        <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
          <div className="flex items-center gap-2">
            <Wine className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">{t("events.drinkMenu")}</h2>
          </div>

          {drinks.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">{t("events.noDrinkMenu")}</p>
          ) : (
            <div className="mt-4 space-y-2">
              {drinks.map((d) => {
                const qty = drinkQty[d.id] ?? 0;
                return (
                  <div key={d.id} className="flex items-center justify-between gap-3 rounded-xl border border-border p-3">
                    <div className="min-w-0">
                      <div className="font-medium">{tc(d.name)}</div>
                      {d.description && <div className="truncate text-xs text-muted-foreground">{tc(d.description)}</div>}
                      <div className="mt-0.5 text-sm text-muted-foreground">
                        {(d.price_kurus / 100).toLocaleString(locale)} ₺
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button type="button" variant="outline" size="icon" onClick={() => setQty(d.id, -1)} disabled={qty === 0}>
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-6 text-center font-medium">{qty}</span>
                      <Button type="button" variant="outline" size="icon" onClick={() => setQty(d.id, 1)}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{t("events.totalItems", { count: totalItems })}</span>
            <span className="text-2xl font-bold">{(totalKurus / 100).toLocaleString(locale)} ₺</span>
          </div>

          <div className="mt-5 rounded-xl border border-border bg-muted/20 p-3">
            <div className="flex items-center gap-2">
              <Coins className="h-5 w-5 text-primary" />
              <div className="flex-1">
                <div className="font-medium">{t("events.payWithJ")}</div>
                <div className="text-xs text-muted-foreground">
                  {t("events.jRequiredInfo", { needed: pointsNeeded, balance })}
                </div>
              </div>
              {balance < pointsNeeded && (
                <span className="text-xs font-medium text-destructive">{t("events.insufficient")}</span>
              )}
            </div>
          </div>

          <Button
            onClick={handleCheckout}
            disabled={submitting || totalItems === 0 || balance < pointsNeeded}
            className="mt-4 w-full"
            size="lg"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("events.payWithJ")}
          </Button>
          {balance < pointsNeeded && (
            <p className="mt-3 text-center text-xs text-muted-foreground">
              {t("events.topupHint")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
